import { createContext, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { APIProvider } from './apiContext';
import RGProvider from './rgContext';

import { APIHandler as API } from '../api';
import { gwAPIHandler as gwAPI } from '../gw';
import { APIProxyHandler } from '../apiProxy';

import { useAPIHandler } from '../hooks/useAPIHandler';

interface ProjectConfig {
  API_BASE_URL: string | null;
  WS_BASE_URL: string | null;
  GW_BASE_URL: string | null;
  GW_PROXY_URL: string | null;
  PROXY_API_BASE_URL: string | null;
}

interface ProjectMetadata {
  projectName: string | null;
  projectURL: string | null;
  projectHmac?: string;
  extraMeta: {
    application_name?: string;
    /** Project identifier (UUID) - new field */
    project_id?: string;
    /** @deprecated Use project_id instead. Kept for backwards compatibility with old projects. */
    project_name?: string;
    project_description?: string;
    application_description?: string;
    application_category?: string;
    initialization_status?: string;
    creation_time?: string;
  };
}

type APIHandlerType = ReturnType<typeof API>;
type GwAPIHandlerType = ReturnType<typeof gwAPI>;
type ProxyHandlerType = ReturnType<typeof APIProxyHandler>;

type ProxyFetchType = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

type ProxyGetViewType = (
  schema: string,
  viewName: string,
  parameters: { [key: string]: string | number | boolean }
) => Promise<{ [key: string]: any }[]>;

interface ProjectContextValue {
  config: ProjectConfig;
  projectMetadata: ProjectMetadata;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  syncToken: string;
  setSyncToken: () => void;
  selectedComponentId: string;
  setSelectedComponentId: React.Dispatch<React.SetStateAction<string>>;
  selectedComponentXPath: string;
  setSelectedComponentXPath: React.Dispatch<React.SetStateAction<string>>;
  selectedComponentHTML: string;
  setSelectedComponentHTML: React.Dispatch<React.SetStateAction<string>>;
  isComponentSubselected: boolean;
  setIsComponentSubselected: React.Dispatch<React.SetStateAction<boolean>>;
  APIHandler: APIHandlerType;
  gwAPIHandler: GwAPIHandlerType;
  proxyHandler: ProxyHandlerType;
  proxyFetch: ProxyFetchType | null;
  proxyGetView: ProxyGetViewType | null;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);
const ProjectConsumer = ProjectContext.Consumer;

// Inner provider that uses the API context
const ProjectProviderInner = (props: {
  config: ProjectConfig;
  metadata?: ProjectMetadata;
  children?: React.ReactNode;
}) => {
  // Group 1: All useState hooks
  const [projectMetadata, setProjectMetadata] = useState<ProjectMetadata>({
    projectName: null,
    projectURL: null,
    extraMeta: {},
  });
  const [config, setConfig] = useState<ProjectConfig>({
    API_BASE_URL: null,
    WS_BASE_URL: null,
    GW_BASE_URL: null,
    GW_PROXY_URL: null,
    PROXY_API_BASE_URL: null,
  });
  const [gwAPIHandler, setgwAPIHandler] = useState<GwAPIHandlerType>(() =>
    gwAPI(props.config.GW_BASE_URL || '')
  );
  const [isComponentSubselected, setIsComponentSubselected] = useState<boolean>(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string>('');
  const [selectedComponentXPath, setSelectedComponentXPath] = useState<string>('');
  const [selectedComponentHTML, setSelectedComponentHTML] = useState<string>('');
  const [syncToken, _setSyncToken] = useState<string>(uuid());

  // Group 2: API Handlers
  const wrappedAPIHandler = useAPIHandler(props.config.API_BASE_URL || '');

  // Regular handler creation (not in a hook)
  const fallbackAPIHandler = API(props.config.API_BASE_URL || '');

  // Conditional selection moved outside hooks
  const APIHandler = wrappedAPIHandler || fallbackAPIHandler;

  const [proxyHandler, setProxyHandler] = useState<ProxyHandlerType>(() =>
    APIProxyHandler(props.config.PROXY_API_BASE_URL || '', projectMetadata?.projectHmac)
  );
  const [proxyFetch, setProxyFetch] = useState<ProxyFetchType | null>(null);
  const [proxyGetView, setProxyGetView] = useState<ProxyGetViewType | null>(null);

  const setSyncToken = () => {
    _setSyncToken(uuid());
  };

  // Group 3: useEffect hooks
  useEffect(() => {
    setConfig(props?.config);
  }, [props.config]);

  useEffect(() => {
    if (props?.metadata) {
      setProjectMetadata(props.metadata);
    }
  }, [props.metadata]);

  useEffect(() => {
    // Only proceed if we have all required config
    if (!config?.PROXY_API_BASE_URL || !config?.GW_PROXY_URL || !projectMetadata?.projectName) {
      console.log('Missing required config for proxy setup');
      return;
    }

    setgwAPIHandler(gwAPI(config.GW_BASE_URL || ''));
    setProxyHandler((_: ProxyHandlerType) => {
      const proxyHandler = APIProxyHandler(
        config.PROXY_API_BASE_URL || '',
        projectMetadata?.projectHmac
      );

      // Wrap proxyFetch to ensure we don't pass Promises
      const proxyFetch = proxyHandler.createProxyFetch({
        miniappName: projectMetadata.projectName || '',
      });
      const safeProxyFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        return proxyFetch(input, init);
      };
      setProxyFetch(() => safeProxyFetch);

      // Wrap getView to ensure we don't pass Promises
      const getView = proxyHandler.createProxyGetView({
        miniappName: projectMetadata.projectName || '',
        gwProxyUrl: config.GW_PROXY_URL || '',
      });
      const safeGetView = async (
        schema: string,
        viewName: string,
        parameters: { [key: string]: string | number | boolean } = {}
      ) => {
        if (typeof schema !== 'string' || typeof viewName !== 'string') {
          throw new Error(
            `Invalid parameters: schema and viewName must be strings. Got schema: ${typeof schema}, viewName: ${typeof viewName}`
          );
        }
        return getView(schema, viewName, parameters);
      };
      setProxyGetView(() => safeGetView);

      return proxyHandler;
    });
  }, [config, projectMetadata]);

  return (
    <ProjectContext.Provider
      value={{
        config,
        projectMetadata,
        setProjectMetadata,
        syncToken,
        setSyncToken,
        selectedComponentId,
        setSelectedComponentId,
        selectedComponentXPath,
        setSelectedComponentXPath,
        selectedComponentHTML,
        setSelectedComponentHTML,
        isComponentSubselected,
        setIsComponentSubselected,
        APIHandler,
        gwAPIHandler,
        proxyHandler,
        proxyFetch,
        proxyGetView,
      }}
    >
      {props.children}
    </ProjectContext.Provider>
  );
};

// Outer provider that wraps the inner provider with APIProvider
const ProjectProvider = (props: {
  config: ProjectConfig;
  metadata?: ProjectMetadata;
  children?: React.ReactNode;
}) => {
  return (
    <APIProvider>
      <RGProvider>
        <ProjectProviderInner {...props} />
      </RGProvider>
    </APIProvider>
  );
};

export { ProjectContext, ProjectProvider, type ProjectContextValue };
